import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useI18n } from '../../../i18n/useI18n';
import { LiksDropdown } from '../LiksDropdown/LiksDropdown';
import { LiksIconButton } from '../LiksIconButton/LiksIconButton';
import './LiksPagination.css';

export type LiksPaginationProps = {
  className?: string;
  page: number;
  /** When set, the page label uses `common.pagination.pageOf`. */
  totalPages?: number | null;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  /** Page size dropdown — omit `pageSizes` and `onPageSizeChange` to hide. */
  pageSize?: number;
  pageSizes?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** First/last page buttons — set `showBoundaries` and provide handlers. */
  showBoundaries?: boolean;
  canGoToFirstPage?: boolean;
  canGoToLastPage?: boolean;
  onFirstPage?: () => void;
  onLastPage?: () => void;
};

export function LiksPagination({
  className,
  page,
  totalPages = null,
  hasPreviousPage,
  hasNextPage,
  onPreviousPage,
  onNextPage,
  pageSize,
  pageSizes,
  onPageSizeChange,
  showBoundaries = false,
  canGoToFirstPage = false,
  canGoToLastPage = false,
  onFirstPage,
  onLastPage,
}: LiksPaginationProps) {
  const { translate } = useI18n();

  const showPageSize =
    pageSizes != null &&
    pageSizes.length > 0 &&
    onPageSizeChange != null &&
    pageSize != null;

  const pageLabel =
    totalPages != null
      ? translate('common.pagination.pageOf', {
          current: String(page),
          total: String(totalPages),
        })
      : `${translate('common.pagination.page')} ${page}`;

  const handlePageSizeChange = (value: string) => {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) {
      return;
    }
    onPageSizeChange?.(parsedValue);
  };

  return (
    <div className={['liks-pagination', className].filter(Boolean).join(' ')}>
      {showPageSize ? (
        <div className="liks-pagination__group">
          <span className="liks-pagination__label">
            {translate('common.pagination.pageSize')}
          </span>
          <LiksDropdown
            className="liks-pagination__page-size"
            variant="on-light"
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            options={pageSizes.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
            aria-label={translate('common.pagination.pageSize')}
          />
        </div>
      ) : null}
      <div className="liks-pagination__group">
        <span className="liks-pagination__page">{pageLabel}</span>
        {showBoundaries ? (
          <LiksIconButton
            className="liks-pagination__button"
            aria-label={translate('common.pagination.firstPage')}
            disabled={!canGoToFirstPage}
            onClick={onFirstPage}
          >
            <ChevronsLeft aria-hidden />
          </LiksIconButton>
        ) : null}
        <LiksIconButton
          className="liks-pagination__button"
          aria-label={translate('common.pagination.previousPage')}
          disabled={!hasPreviousPage}
          onClick={onPreviousPage}
        >
          <ChevronLeft aria-hidden />
        </LiksIconButton>
        <LiksIconButton
          className="liks-pagination__button"
          aria-label={translate('common.pagination.nextPage')}
          disabled={!hasNextPage}
          onClick={onNextPage}
        >
          <ChevronRight aria-hidden />
        </LiksIconButton>
        {showBoundaries ? (
          <LiksIconButton
            className="liks-pagination__button"
            aria-label={translate('common.pagination.lastPage')}
            disabled={!canGoToLastPage}
            onClick={onLastPage}
          >
            <ChevronsRight aria-hidden />
          </LiksIconButton>
        ) : null}
      </div>
    </div>
  );
}
